import {
    Button,
    Group,
} from '@mantine/core';
import {
    AiOutlineCheckCircle,
    AiOutlineStop,
} from 'react-icons/ai';

import {
    TestDefinition,
    TestDefinitionStatus,
} from '../types.ts';
import {useSetTestDefinitionStatusMutation} from '../hooks/useTestDefinitionsQuery.ts';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions.ts';
import {rbacActions} from '../../../config/rbac/actions.ts';

// Status is NOT a field on the edit form. Transitions are explicit affordances
// that call setTestDefinitionStatus — a conditional write on the server. If a
// transition is illegal the mutation fails and the error is surfaced by
// notifyError (wired in useSetTestDefinitionStatusMutation), never swallowed.
//
// Offered transitions:
//   DRAFT   -> ACTIVE  (Activate)
//   ACTIVE  -> RETIRED (Retire)
//   RETIRED -> ACTIVE  (Reactivate) — offered because only the server decides
//                       whether it is legal; if it is not, the user sees why.
export const SetStatusButtons = ({record}: { record: TestDefinition }) => {

    const {allowedActions} = useVerifiedPermissions();
    const setStatusMutation = useSetTestDefinitionStatusMutation();

    if (!allowedActions.includes(rbacActions.SetTestDefinitionStatus)) {
        return null;
    }

    const handleSetStatus = (targetStatus: TestDefinitionStatus) => {
        setStatusMutation.mutate({
                                     id: record.id,
                                     targetStatus: targetStatus,
                                 });
    };

    const buttons = [];

    if (record.status !== TestDefinitionStatus.ACTIVE) {
        buttons.push(
            <Button
                key='activate'
                variant='light'
                color='green'
                leftSection={<AiOutlineCheckCircle/>}
                loading={setStatusMutation.isPending}
                onClick={() => handleSetStatus(TestDefinitionStatus.ACTIVE)}
            >
                {record.status === TestDefinitionStatus.RETIRED ? 'Reactivate' : 'Activate'}
            </Button>
        );
    }

    if (record.status !== TestDefinitionStatus.RETIRED) {
        buttons.push(
            <Button
                key='retire'
                variant='light'
                color='orange'
                leftSection={<AiOutlineStop/>}
                loading={setStatusMutation.isPending}
                onClick={() => handleSetStatus(TestDefinitionStatus.RETIRED)}
            >
                Retire
            </Button>
        );
    }

    if (buttons.length === 0) {
        return null;
    }

    return <Group gap={'xs'}>{buttons}</Group>;
};
